import { DrawerLink } from '../../app/DrawerLink';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { BufferRing } from '../../components/ui/Loader';
import { usePlayer } from '../../stores/player';
import { useRoomTuning } from '../../stores/room';
import { useSession } from '../../stores/session';
import { stopListening } from '../rooms/listening';
import { SubmitDialog } from '../rooms/SubmitDialog';
import { useRoom } from '../rooms/useRoom';

/**
 * The dock, while you are following a broadcast (§6.8).
 *
 * ## Why the dock cannot just be the dock here
 *
 * Every transport control it normally shows is a lie during a broadcast.
 * Scrubbing is undone by `useRoomSync` within a second - everybody in a room is
 * at the same instant, which is what a room *is*. "Next" loads whatever happens
 * to be in the local queue and drops you out of the broadcast without saying
 * so. Even pause is strange: the show carries on without you and you rejoin
 * wherever it has got to, which is not what a pause button means anywhere else.
 *
 * So the controls are not disabled, they are absent, and what replaces them is
 * the two things that are actually true: this is live, and you can leave.
 *
 * ## Getting back
 *
 * A broadcast has no URL of its own - it lives at its host's profile - which is
 * easy to lose once you have navigated three pages away from it. The host's
 * name is the way back, and it stops being a link only when you are already
 * there, where a link to the page you are on is furniture.
 *
 * ## And one thing to do, not just a way out
 *
 * Submitting is here for the same reason the way back is: it was only ever on
 * the host's profile, so hearing something on a room while reading somebody
 * else's page meant navigating back to act on it - at which point the impulse
 * has usually gone. This is the moment an artist decides a host would play
 * their record, and the dock is where they are when they decide it.
 */
export function BroadcastBar({
  username,
  host,
  name,
}: {
  username: string;
  host: string;
  /** What tonight's set is called. Most broadcasts are not called anything. */
  name: string | null;
}) {
  const track = usePlayer((s) => s.track);
  const streaming = useRoomTuning((s) => s.tuned?.streaming ?? null);
  const loading = usePlayer((s) => s.loading);
  const error = usePlayer((s) => s.error);
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);
  const setVolume = usePlayer((s) => s.setVolume);
  const toggleMute = usePlayer((s) => s.toggleMute);

  const { pathname } = useLocation();
  const here = pathname === `/${username}`;

  const me = useSession((state) => state.user);
  const [submitting, setSubmitting] = useState(false);
  const client = useQueryClient();

  /*
   * Never on your own show.
   *
   * `PlayerBar` already keeps this bar away from a host broadcasting from the
   * studio or an external encoder, but a host running their own catalogue is
   * an ordinary listener as far as the dock is concerned - and offering them a
   * dialog to submit to themselves is offering a 422.
   */
  const mine = me?.username === username;

  /*
   * The room, for the dialog that needs all of it.
   *
   * Shares `['room', username]` with `RoomFollower`, which is already polling
   * it for the audio - so this costs nothing and is never behind what the bar
   * beside it is saying. Not asked at all for a signed-out reader or the host,
   * neither of whom is offered the button.
   */
  const { data } = useRoom(username, { listen: false, enabled: Boolean(me) && !mine });
  const room = data?.room ?? null;

  /*
   * Every condition stated here, and none of them left to `enabled`.
   *
   * A disabled query still hands back whatever is in the cache under its key,
   * and `RoomFollower` is filling that key for the audio - so `enabled` is an
   * optimisation about requests and never a guard about what is shown. Written
   * the other way round it silently offered a host the button to submit to
   * their own show.
   *
   * Only the host's own policy hides it beyond that, exactly as the room page
   * decides it: `followers` stays offered, because the room may already count
   * this listener as one and the server is the thing that knows.
   */
  const canSubmit =
    me !== null &&
    !mine &&
    room !== null &&
    room.status === 'live' &&
    room.submissions !== 'closed';

  return (
    <div
      role="region"
      aria-label={`Listening to ${host} live`}
      data-player-dock
      data-broadcast-dock
      className="flex h-full items-center gap-4 px-6"
    >
      <span className="flex shrink-0 items-center gap-2 rounded-[var(--radius-pill)] border border-hot-edge bg-hot-wash px-3 py-1.5">
        {/* The one animated thing in the bar, and only while actually live. */}
        <span className="h-2 w-2 animate-pulse rounded-full bg-hot" aria-hidden />
        <span className="meta-caps text-hot-text">Live</span>
      </span>

      {track ? (
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          {(track.artwork_small ?? track.artwork) ? (
            <img
              src={track.artwork_small ?? track.artwork ?? undefined}
              alt=""
              className="h-11 w-11 rounded-[var(--radius-control)] object-cover"
            />
          ) : (
            <div
              aria-hidden
              className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
              style={{
                backgroundImage: 'repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)',
              }}
            />
          )}

          <div className="min-w-0 max-w-[240px]">
            <DrawerLink
              to={`/tracks/${track.id}`}
              className="block truncate text-sm font-medium hover:underline"
            >
              {track.title}
            </DrawerLink>
            <div className="meta truncate text-faint">{track.owner?.display_name ?? ''}</div>
          </div>
        </div>
      ) : streaming ? (
        /*
          A studio broadcast, where the dock has no player to read.

          The audio is inside the host's stream, so nothing is loaded into the
          player and the branch below would say "between tracks" over a record
          the listener can plainly hear. The room tells us instead - see
          `Tuned.streaming`.
        */
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          <div className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] bg-raised" aria-hidden />
          <div className="min-w-0 max-w-[240px]">
            <div className="truncate text-sm font-medium text-ink">{streaming.title}</div>
            <div className="meta truncate text-faint">{streaming.artist}</div>
          </div>
        </div>
      ) : (
        // A real state, not a gap: the host is between tracks and the room is
        // still theirs. Going blank here would read as the broadcast ending.
        <div className="flex min-w-0 shrink-0 items-center gap-3">
          <div className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] bg-raised" aria-hidden />
          <div className="min-w-0">
            <div className="text-sm text-faint">Between tracks</div>
            <div className="meta text-faint">still on air</div>
          </div>
        </div>
      )}

      {/*
        Whose show it is, which the normal dock has no room for and which
        matters more here than the track does: you can be hearing a stranger's
        record with no idea where the sound is coming from.
      */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="meta shrink-0 text-faint">on</span>

        {here ? (
          <span className="min-w-0 truncate text-sm font-medium text-ink">{host}</span>
        ) : (
          <Link
            to={`/${username}`}
            data-back-to-room
            className="min-w-0 truncate rounded-[var(--radius-pill)] border border-line px-3 py-1.5 text-sm font-medium text-ink transition-colors hover:border-line-strong hover:bg-raised"
          >
            {host}
            <span className="meta ml-2 text-faint">back to the room →</span>
          </Link>
        )}

        {name && <span className="meta hidden truncate text-ghost xl:block">{name}</span>}

        {loading && <BufferRing size={18} />}
        {error && <span className="meta shrink-0 text-hot-text">{error}</span>}
      </div>

      {track && (
        <FavoriteButton
          trackId={track.id}
          favorited={track.favorited}
          likeCount={track.like_count ?? 0}
          size="sm"
          className="shrink-0"
        />
      )}

      <div className="hidden shrink-0 items-center gap-2 lg:flex">
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
          className="text-soft hover:text-ink"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden>
            {muted || volume === 0 ? (
              <path
                d="M7 3L4 6H2v4h2l3 3zM10.5 6l3 4M13.5 6l-3 4"
                stroke="currentColor"
                fill="none"
                strokeWidth="1.4"
              />
            ) : (
              <path
                d="M7 3L4 6H2v4h2l3 3zM10 5.5a3.5 3.5 0 010 5M12 3.5a6.5 6.5 0 010 9"
                stroke="currentColor"
                fill="none"
                strokeWidth="1.4"
              />
            )}
          </svg>
        </button>

        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          onChange={(event) => setVolume(Number(event.target.value))}
          aria-label="Volume"
          className="h-1 w-20 accent-[var(--color-signal)]"
        />
      </div>

      {/*
        Before "stop listening", because it is the thing somebody is more
        likely to want - and hidden on a narrow dock, where the bar is already
        fighting for room and the host's profile is one tap away.
      */}
      {canSubmit && (
        <button
          type="button"
          onClick={() => setSubmitting(true)}
          data-dock-submit
          className="hidden shrink-0 rounded-[var(--radius-pill)] border border-line px-4 py-2 text-sm text-ink transition-colors hover:border-accent hover:text-accent md:block"
        >
          Submit a track
        </button>
      )}

      <button
        type="button"
        onClick={stopListening}
        data-stop-listening
        className="shrink-0 rounded-[var(--radius-pill)] border border-line px-4 py-2 text-sm text-soft transition-colors hover:border-hot-edge hover:text-hot-text"
      >
        Stop listening
      </button>

      {submitting && room && me && (
        <SubmitDialog
          room={room}
          username={me.username}
          onClose={() => setSubmitting(false)}
          onSubmitted={() => {
            setSubmitting(false);
            // The queue strip on the host's page is the thing that just
            // changed, and somebody who submitted from the dock is quite
            // likely looking at it.
            void client.invalidateQueries({ queryKey: ['room', username] });
          }}
        />
      )}
    </div>
  );
}
